import { Navigate, Route, Routes } from "react-router-dom";
import DevelopmentBanner from "./components/DevelopmentBanner";
import Layout from "./components/Layout";
import LoadingSpinner from "./components/LoadingSpinner";
import { ThemeProvider } from "./contexts/ThemeContext";
import { AuthProvider, useAuth } from "./hooks/useAuth";
import ApplicationDetailPage from "./pages/ApplicationDetailPage";
import ApplicationsPage from "./pages/ApplicationsPage";
import AssessmentImportPage from "./pages/AssessmentImportPage";
import ApprovalsPage from "./pages/ApprovalsPage";
import AuthCallbackPage from "./pages/AuthCallbackPage";
import BillingPage from "./pages/BillingPage";
import BillingSummaryPage from "./pages/BillingSummaryPage";
import CRAAssessmentPage from "./pages/CRAAssessmentPage";
import CRAComplianceAssessmentPage from "./pages/CRAComplianceAssessmentPage";
import CreateApplicationPage from "./pages/CreateApplicationPage";
import CreateDtApiKeyPage from "./pages/CreateDtApiKeyPage";
import CreateProductPage from "./pages/CreateProductPage";
import CreateSecurityAdvisoryPage from "./pages/CreateSecurityAdvisoryPage";
import DashboardPage from "./pages/DashboardPage";
import DataActAssessmentPage from "./pages/DataActAssessmentPage";
import DtApiKeysPage from "./pages/DtApiKeysPage";
import EditApplicationPage from "./pages/EditApplicationPage";
import EditDtApiKeyPage from "./pages/EditDtApiKeyPage";
import EditProductPage from "./pages/EditProductPage";
import EditSecurityAdvisoryPage from "./pages/EditSecurityAdvisoryPage";
import FeatureRequestsPage from "./pages/FeatureRequestsPage";
import RoadmapPage from "./pages/RoadmapPage";
import GithubCopilotDashboardPage from "./pages/GithubCopilotDashboardPage";
import LicensePage from "./pages/LicensePage";
import LicenseRequestsPage from "./pages/LicenseRequestsPage";
import LoginPage from "./pages/LoginPage";
import OnboardingRequestsPage from "./pages/OnboardingRequestsPage";
import ProductDetailPage from "./pages/ProductDetailPage";
import ProductsPage from "./pages/ProductsPage";
import RepositoriesPage from "./pages/RepositoriesPage";
import RepositoryDetailPage from "./pages/RepositoryDetailPage";
import SDLAssessmentPageSimple from "./pages/SDLAssessmentPageSimple";
import SDLAssessmentResultsPage from "./pages/SDLAssessmentResultsPage";
import SecurityAdvisoriesPage from "./pages/SecurityAdvisoriesPage";
import UsageCapsPage from "./pages/UsageCapsPage";
import UsersPage from "./pages/UsersPage";
import ThreatModellingPage from "./pages/ThreatModellingPage";
import ThreatModelsListPage from "./pages/ThreatModelsListPage";
import { isProdAtlasProduct } from "./utils/env";

function AppRoutes() {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen">
        <DevelopmentBanner />
        <div className="flex items-center justify-center min-h-[calc(100vh-3rem)]">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  // Public routes (accessible without authentication)
  const publicRoutes = (
    <div className="min-h-screen">
      <DevelopmentBanner />
      <Routes>
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
        <Route path="*" element={<LoginPage />} />
      </Routes>
    </div>
  );

  if (!user) {
    return publicRoutes;
  }

  return (
    <div className="min-h-screen">
      <DevelopmentBanner />
      <Layout>
        <Routes>
          <Route
            path="/"
            element={
              <Navigate
                to={isProdAtlasProduct() ? "/dashboard" : "/license"}
                replace
              />
            }
          />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/products" element={<ProductsPage />} />
          <Route path="/products/create" element={<CreateProductPage />} />
          <Route path="/products/:id" element={<ProductDetailPage />} />
          <Route path="/products/:id/edit" element={<EditProductPage />} />
          <Route
            path="/products/:id/cra-assessment"
            element={<CRAAssessmentPage />}
          />
          <Route
            path="/products/:id/cra-compliance"
            element={<CRAComplianceAssessmentPage />}
          />
          <Route
            path="/products/:id/sdl-assessment"
            element={<SDLAssessmentPageSimple />}
          />
          <Route
            path="/products/:id/sdl-results"
            element={<SDLAssessmentResultsPage />}
          />
          <Route
            path="/products/:id/data-act-assessment"
            element={<DataActAssessmentPage />}
          />
          <Route
            path="/assessments/import"
            element={<AssessmentImportPage />}
          />
          <Route path="/repositories" element={<RepositoriesPage />} />
          <Route
            path="/repositories/:owner/:name"
            element={<RepositoryDetailPage />}
          />
          <Route path="/applications" element={<ApplicationsPage />} />
          <Route
            path="/applications/create"
            element={<CreateApplicationPage />}
          />
          <Route path="/applications/:id" element={<ApplicationDetailPage />} />
          <Route
            path="/applications/:id/edit"
            element={<EditApplicationPage />}
          />
          <Route path="/dt-api-keys" element={<DtApiKeysPage />} />
          <Route path="/dt-api-keys/create" element={<CreateDtApiKeyPage />} />
          <Route path="/dt-api-keys/:id/edit" element={<EditDtApiKeyPage />} />
          <Route path="/threat-modelling" element={<ThreatModelsListPage />} />
          <Route
            path="/threat-modelling/create"
            element={<ThreatModellingPage key="create" />}
          />
          <Route
            path="/threat-modelling/:id"
            element={<ThreatModellingPage key="edit" />}
          />
          <Route
            path="/security-advisories"
            element={<SecurityAdvisoriesPage />}
          />
          <Route
            path="/security-advisories/create"
            element={<CreateSecurityAdvisoryPage />}
          />
          <Route
            path="/security-advisories/:id/edit"
            element={<EditSecurityAdvisoryPage />}
          />
          <Route
            path="/github/copilot"
            element={<GithubCopilotDashboardPage />}
          />
          <Route path="/billing/invoices" element={<BillingPage />} />
          <Route path="/billing/summary" element={<BillingSummaryPage />} />
          <Route path="/billing/usage-caps" element={<UsageCapsPage />} />
          <Route
            path="/billing/license-requests"
            element={<LicenseRequestsPage />}
          />
          {!isProdAtlasProduct() && (
            <Route path="/license" element={<LicensePage />} />
          )}
          {!isProdAtlasProduct() && (
            <Route path="/feature-requests" element={<FeatureRequestsPage />} />
          )}
          {!isProdAtlasProduct() && (
            <>
              <Route path="/roadmap" element={<RoadmapPage />} />
              <Route path="/roadmap/manage" element={<RoadmapPage />} />
            </>
          )}
          {!isProdAtlasProduct() && (
            <>
              <Route path="/onboarding" element={<OnboardingRequestsPage />} />
              <Route
                path="/onboarding/new"
                element={<OnboardingRequestsPage />}
              />
              <Route
                path="/onboarding/new/:typeSlug"
                element={<OnboardingRequestsPage />}
              />
            </>
          )}
          <Route path="/approvals" element={<ApprovalsPage />} />
          <Route path="/users" element={<UsersPage />} />
          <Route
            path="*"
            element={
              <Navigate
                to={isProdAtlasProduct() ? "/dashboard" : "/license"}
                replace
              />
            }
          />
        </Routes>
      </Layout>
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
